import { Paginator } from "@/components/paginator";
import { PayByLinkForm } from "@/routes/(app)/_main-layout/_pay-by-link/-components/form";
import { StatusTag } from "@/components/status-tag";
import { EU } from "@/components/svg/eu";
import { Gbp } from "@/components/svg/gbp";
import { CardWithHeader } from "@/components/card-with-header";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { hasRole } from "@/lib/auth";
import { numberToDecimalWithCurrency } from "@/lib/utils";
import { VolumeApi } from "@/lib/volume-api";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { MoreHorizontal, Eye } from "lucide-react";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod/v4";

export const Route = createFileRoute(
  "/(app)/_main-layout/_pay-by-link/pay-by-link",
)({
  validateSearch: zodValidator(
    z.object({ page: z.number().optional().catch(1).default(1) }),
  ),
  component: () => <PayByLinkComponent />,
  beforeLoad: ({ context }) => {
    if (!hasRole("PAY_BY_LINK", context.authorities)) {
      throw redirect({ to: "/", search: true });
    }
    return context;
  },
  loaderDeps: ({ search: { page } }) => ({ page }),
  loader: ({ context: { selectedApplicationId, accessToken }, deps }) => {
    const api = new VolumeApi(accessToken, selectedApplicationId);
    return api.getRecentPaymentIntents({
      applicationId: selectedApplicationId,
      page: deps.page - 1,
    });
  },
});

const PayByLinkComponent = () => {
  const recentPaymentLinks = Route.useLoaderData();

  return (
    <>
      <div className="mt-4">
        <CardWithHeader title="Generate New Payment Link">
          <PayByLinkForm />
        </CardWithHeader>
      </div>
      <div className="my-8 w-full">
        <CardWithHeader title="Recent Payment Links" noChildPadding>
          <table className="w-full text-left text-sm text-gray-500 my-3">
            <thead className="border-b-2 text-xs uppercase text-gray-700">
              <tr>
                <th scope="col" className="px-6 py-3 w-1/5">
                  Merchant Payment ID
                </th>
                <th scope="col" className="px-6 py-3 w-1/5">
                  Currency
                </th>
                <th scope="col" className="px-6 py-3 w-1/5">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 w-1/5">
                  Expires at
                </th>
                <th scope="col" className="px-6 py-3 w-1/5">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {recentPaymentLinks.page.numberOfElements === 0 ? (
                <tr className="w-full text-center text-base font-normal">
                  <td className="pt-5" colSpan={5}>
                    No payments found
                  </td>
                </tr>
              ) : null}
              {recentPaymentLinks.page.content.map((paymentIntent) => (
                <tr key={paymentIntent.paymentIntentId} className="bg-white ">
                  <td className="px-6 py-4">
                    <Link
                      className="text-blue-700 underline"
                      to="/pay-by-link/$paymentIntentId"
                      params={{
                        paymentIntentId: paymentIntent.paymentIntentId,
                      }}
                      search
                    >
                      {paymentIntent.merchantPaymentId}
                    </Link>
                  </td>
                  <td className="flex items-center px-6 py-4 gap-2">
                    {paymentIntent.paymentRequest.currency === "GBP" ? (
                      <Gbp />
                    ) : (
                      <EU />
                    )}
                    {paymentIntent.paymentRequest.currency}
                  </td>
                  <td className="px-6 py-4">
                    {numberToDecimalWithCurrency(
                      paymentIntent.paymentRequest.amount,
                      paymentIntent.paymentRequest.currency,
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {new Date(paymentIntent.expiryTimeUtc).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 pr-40 w-20">
                    {paymentIntent.activePaymentStatus ? (
                      <StatusTag
                        paymentStatus={
                          paymentIntent.activePaymentStatus as never
                        }
                      />
                    ) : (
                      <div />
                    )}
                  </td>
                  <td className="pr-10">
                    {(paymentIntent.activePaymentStatus === "COMPLETED" ||
                      paymentIntent.activePaymentStatus === "SETTLED") &&
                    paymentIntent.activePaymentId ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger className="focus:outline-hidden">
                          <MoreHorizontal className="rounded-xl hover:bg-volume-text-purple/20" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <Link
                            to="/payments/$paymentId"
                            params={{
                              paymentId: paymentIntent.activePaymentId,
                            }}
                            search
                            className="flex"
                          >
                            <DropdownMenuItem className="hover:cursor-pointer">
                              <Eye className="ml-1 mr-4" /> Details
                            </DropdownMenuItem>
                          </Link>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="py-2 pb-5">
            <Paginator
              from={Route.id}
              totalPages={recentPaymentLinks.page.totalPages}
            />
          </div>
        </CardWithHeader>
      </div>
    </>
  );
};
